import AlertMessage from '@components/alerts/AlertMessage';
import Button from '@components/button/Button';
import { ContactUs } from '@components/contactUsLink/ContactUs';
import InputWithFormItem from '@components/inputWithFormItem/InputWithFormItem';
import { CenterPanel } from '@components/layout/CenterPanel/CenterPanel';
import { SurfaceContainer } from '@components/layout/surfaceContainer/SurfaceContainer';
import { BrandLogo } from '@components/logos/brandLogo/BrandLogo';
import { Form, Space, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { MdOutlineMailOutline } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import { firebasePostApi } from '../../api/firebaseApi';

const { Title, Text } = Typography;

const SignInMagicLink = () => {
  const { t } = useTranslation('common', {
    keyPrefix: 'loginAndSignup',
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setIsError(false);
    setUserEmail(values.email);

    try {
      await firebasePostApi('/user/sign-in/token/send', {
        email: values.email,
      });
      setSuccess(true);
    } catch (_error) {
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    setSuccess(false);
    navigate('/');
  };

  const renderSuccessMessage = () => (
    <>
      <Title level={2} className="!tw-m-0 tw-text-center">
        {t('checkYourEmailHeader')}
      </Title>

      <Text className="tw-text-center">
        <Trans
          components={{
            text: <Text className="tw-text-[var(--color-text-weaker)]" />,
            color: <strong style={{ color: 'var(--color-text-primary)' }} />,
          }}
        >
          {t('sentLoginLinkText', { userEmail })}
        </Trans>
      </Text>

      <Button
        type="primary"
        label={t('goBackToLogin')}
        size="middle"
        onClick={handleSuccess}
        fullWidth
      />
    </>
  );

  const renderSendLinkForm = () => (
    <>
      <Title level={2} className="!tw-m-0 tw-text-center">
        {t('enterEmailHeader')}
      </Title>

      <Text className="tw-text-[--color-text-weaker] tw-flex tw-text-center">
        {t('enterEmailText')}
      </Text>

      <Form
        size="middle"
        layout="vertical"
        onFinish={handleSubmit}
        className="tw-flex tw-flex-col tw-gap-2"
      >
        <InputWithFormItem
          inputName="email"
          size="middle"
          placeholderText={t('emailInputPlaceholder')}
          rules={[
            {
              required: true,
              message: t('emailIsRequired'),
            },
            { type: 'email', message: t('emailIsNotValid') },
          ]}
          PrefixIcon={MdOutlineMailOutline}
        />

        <Button
          submit
          fullWidth
          size="middle"
          type="primary"
          label={t('sendEmailBtn')}
          loading={loading}
        />

        <Button
          fullWidth
          size="middle"
          type="outline"
          label={t('goBackToLogin')}
          onClick={() => {
            navigate('/');
          }}
        />
      </Form>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Sign in link</title>
      </Helmet>
      <CenterPanel>
        <div>
          {isError && (
            <AlertMessage
              className="tw-my-2"
              showIcon
              message={t('somethingWentWrong')}
            />
          )}
          <SurfaceContainer maxWidth={530} noBorder>
            <Space orientation="vertical" size="small" className="tw-p-[16px]">
              <BrandLogo
                size={56}
                wrapperClassName="tw-flex tw-justify-center"
              />

              {success ? renderSuccessMessage() : renderSendLinkForm()}

              <ContactUs wrapperClass="tw-flex tw-justify-center" page="magic-link" />
            </Space>
          </SurfaceContainer>
        </div>
      </CenterPanel>
    </>
  );
};

export default SignInMagicLink;
